import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { TodoWithMeta, TodoStatus } from '../../types/todo.js';
import { TodoStateController } from '../../services/todo-state.js';

interface BoardColumn {
  key: TodoStatus;
  label: string;
}

const COLUMNS: BoardColumn[] = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];

@customElement('cp-todo-board')
export class CpTodoBoard extends LitElement {
  createRenderRoot() { return this; }

  private _todoState = new TodoStateController(this);

  @property({ type: Number }) pseudoProjectId = 0;

  updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has('pseudoProjectId') && this.pseudoProjectId) {
      void this._todoState.loadForProject(this.pseudoProjectId);
    }
  }

  private _onStatusChange(e: CustomEvent): void {
    const { todoId, status } = e.detail as { todoId: number; status: TodoStatus };
    void this._todoState.updateStatus(todoId, status);
  }

  private _onCreateClick(): void {
    this.dispatchEvent(new CustomEvent('todo-create-request', {
      bubbles: true,
      composed: true,
      detail: { pseudoProjectId: this.pseudoProjectId },
    }));
  }

  private _renderEmpty() {
    return html`
      <div class="cp-todo-board__empty">
        <p class="cp-todo-board__empty-text">Noch keine To-Dos angelegt</p>
        <button class="cp-todo-board__empty-btn" @click=${this._onCreateClick}>
          Erstes To-Do erstellen
        </button>
      </div>
    `;
  }

  private _renderColumn(column: BoardColumn, todos: TodoWithMeta[]) {
    return html`
      <div class="cp-todo-board__column">
        <div class="cp-todo-board__column-header">
          <span class="cp-todo-board__column-title">${column.label}</span>
          <span class="cp-todo-board__column-count">${todos.length}</span>
        </div>
        <div class="cp-todo-board__column-body">
          ${todos.map(todo => html`
            <cp-todo-card
              .todo=${todo}
              @todo-status-change=${this._onStatusChange}
            ></cp-todo-card>
          `)}
        </div>
      </div>
    `;
  }

  render() {
    const { loadingState, error, todos } = this._todoState;

    if (!this.pseudoProjectId) return nothing;

    if (loadingState === 'loading' && todos.length === 0) {
      return html`<div class="cp-todo-board__loading">Lade To-Dos...</div>`;
    }

    if (error) {
      return html`<div class="cp-todo-board__error">${error}</div>`;
    }

    if (loadingState === 'loaded' && todos.length === 0) {
      return this._renderEmpty();
    }

    return html`
      <div class="cp-todo-board__toolbar">
        <button class="cp-todo-board__add-btn" @click=${this._onCreateClick}>
          + Neues To-Do
        </button>
      </div>
      <div class="cp-todo-board__grid">
        ${COLUMNS.map(col => this._renderColumn(col, this._todoState.todosByStatus(col.key)))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-todo-board': CpTodoBoard;
  }
}
